/* AIMS Pack catalog page — /aims
   Owner spec (Feb 13, 2026): a one-time €9,90 unlock of 21 specialty PDFs.
   Unauthenticated/non-pack users see the marketing CTA; pack owners see
   the full grid with individual download buttons.
*/
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  BookOpen, Download, Lock, ShieldCheck, FileText, Crown, ArrowRight,
  Loader2, Sparkles,
} from "lucide-react";
import { apiGet } from "../lib/api";

const FADE = {
  hidden: { opacity: 0, y: 14 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

export default function AIMSPack() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    apiGet("/aims/catalog")
      .then(setData)
      .catch((e) => toast.error(e.message || "Errore caricamento"))
      .finally(() => setLoading(false));
  }, []);

  const handleDownload = async (slug) => {
    if (!data?.has_pack) {
      if (data?.stripe_url) {
        window.open(data.stripe_url, "_blank", "noopener,noreferrer");
      }
      return;
    }
    setDownloading(slug);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      const url = `${apiBase}/api/aims/download/${slug}`;
      // Browser navigates; FastAPI returns the PDF with Content-Disposition
      // attachment so it downloads instead of opening inline.
      window.location.href = url;
    } finally {
      setTimeout(() => setDownloading(null), 1500);
    }
  };

  const handleBuy = () => {
    if (data?.stripe_url) {
      window.open(data.stripe_url, "_blank", "noopener,noreferrer");
    } else {
      toast.info("Stripe non ancora configurato. Riprova tra poco.");
    }
  };

  if (loading) {
    return (
      <div className="paper text-brand-primary min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-brand-secondary" />
      </div>
    );
  }
  if (!data) return null;

  const items = data.items || [];
  const hasPack = data.has_pack;

  return (
    <div className="paper text-brand-primary min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="aims-page">
        {/* ─────────── HERO ─────────── */}
        <motion.section initial="hidden" animate="show" variants={FADE}
                        className="relative overflow-hidden rounded-3xl mb-8"
                        style={{
                          background:
                            "linear-gradient(135deg, #7A1322 0%, #9E1B32 35%, #B8862F 100%)",
                        }}>
          {/* Tricolor strip */}
          <div className="h-1.5 w-full flex" aria-hidden="true">
            <div className="flex-1" style={{ background: "#008C45" }} />
            <div className="flex-1" style={{ background: "#FFFFFF" }} />
            <div className="flex-1" style={{ background: "#CD212A" }} />
          </div>
          <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
               style={{ backgroundImage:
                 "radial-gradient(circle at 90% 90%, rgba(255,220,130,0.5) 0%, transparent 45%)" }} />
          <div className="relative px-6 sm:px-10 py-9 sm:py-12 text-white">
            <p className="text-[11px] uppercase tracking-[0.28em] mb-3 inline-flex items-center gap-2"
               style={{ color: "#F4D58D" }}>
              <BookOpen className="h-3.5 w-3.5" /> Pacchetto AIMS
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight">
              21 manuali AIMS
            </h1>
            <p className="font-serif text-xl sm:text-2xl italic mt-1"
               style={{ color: "#F4D58D" }}>
              Ripasso per specialità
            </p>
            <p className="mt-5 max-w-2xl text-base sm:text-lg leading-relaxed"
               style={{ color: "rgba(255,255,255,0.86)" }}>
              I manuali AIMS aggiornati per ogni specialità del bando ministeriale.
              Cardiologia, Chirurgia, Med. Legale, Pediatria, Gineco e tanti altri —
              tutti in italiano, scaricabili in PDF per studiare offline.
            </p>

            {hasPack ? (
              <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold"
                   style={{ background: "rgba(255,255,255,0.14)",
                            border: "1px solid rgba(244,213,141,0.45)",
                            color: "#FFFBEE" }}
                   data-testid="aims-unlocked-chip">
                <ShieldCheck className="h-4 w-4" style={{ color: "#F4D58D" }} />
                Accesso completo · {items.filter(i => i.available).length} manuali sbloccati
              </div>
            ) : (
              <button onClick={handleBuy}
                      data-testid="aims-buy-cta"
                      className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-semibold transition-all hover:scale-[1.03]"
                      style={{
                        background: "linear-gradient(135deg, #FFFBEE 0%, #FFE9B0 100%)",
                        color: "#7A1322",
                        boxShadow: "0 10px 28px -10px rgba(0,0,0,0.45)",
                        border: "1px solid rgba(184,134,47,0.45)",
                      }}>
                <Crown className="h-5 w-5" style={{ color: "#B8862F" }} />
                <span className="font-serif text-lg">Sblocca per €{(data.price_eur || 9.9).toFixed(2).replace(".", ",")}</span>
                <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </motion.section>

        {/* ─────────── PAYWALL EXPLAINER (only if not unlocked) ─────────── */}
        {!hasPack && (
          <div className="mb-8 rounded-2xl border p-5 sm:p-6 flex items-start gap-4"
               style={{ borderColor: "#E5DBC8",
                        background: "linear-gradient(135deg, #FFFBEE 0%, #FBF7EE 100%)" }}>
            <Sparkles className="h-6 w-6 text-brand-gold mt-1 shrink-0" />
            <div className="flex-1">
              <h3 className="font-serif text-xl text-brand-primary mb-1">
                Un pagamento, accesso permanente
              </h3>
              <p className="text-sm text-brand-secondary">
                Con un singolo pagamento di <strong>€9,90</strong> ottieni i 21 manuali
                per sempre. Nessun abbonamento. Scarichi i PDF quando vuoi, anche dopo
                aver superato l'esame.
              </p>
            </div>
          </div>
        )}

        {/* ─────────── CATALOG GRID ─────────── */}
        <h2 className="font-serif text-2xl text-brand-primary mb-4 flex items-center gap-2">
          <FileText className="h-5 w-5 text-brand-secondary" />
          Catalogo manuali ({items.length})
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
             data-testid="aims-grid">
          {items.map((it) => (
            <ManualCard key={it.slug} item={it} hasPack={hasPack}
                        downloading={downloading === it.slug}
                        onAction={() => handleDownload(it.slug)} />
          ))}
        </div>

        {/* ─────────── FOOTER NOTE ─────────── */}
        <p className="mt-8 text-xs text-brand-secondary text-center">
          {hasPack
            ? "I download sono registrati per evitare abusi. Grazie per il rispetto dei contenuti."
            : "Pagamento sicuro tramite Stripe. Ricevi accesso istantaneo + email di conferma."}
        </p>
      </div>
    </div>
  );
}

function ManualCard({ item, hasPack, downloading, onAction }) {
  const locked = !hasPack || !item.available;
  return (
    <button
      onClick={onAction}
      disabled={downloading || !item.available}
      data-testid={`aims-card-${item.slug}`}
      className="group relative text-left rounded-2xl border bg-white p-4 transition-all hover:-translate-y-0.5 hover:shadow-premium disabled:opacity-60"
      style={{
        borderColor: locked ? "#E5DBC8" : "rgba(0,140,69,0.30)",
      }}>
      {/* Specialty badge */}
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl grid place-items-center shrink-0"
             style={{
               background: locked ? "rgba(122,110,87,0.10)" : "rgba(0,140,69,0.10)",
               color:      locked ? "#7a6e57" : "#008C45",
               fontWeight: 800,
               fontSize:   item.code.length > 3 ? "11px" : "13px",
               letterSpacing: "0.04em",
             }}>
          {item.code}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-serif text-base leading-tight text-brand-primary">
            {item.label_it}
          </p>
          <p className="text-xs text-brand-secondary mt-0.5">
            PDF · {item.size_mb != null ? `${item.size_mb} MB` : "—"}
          </p>
        </div>
      </div>
      {/* CTA strip */}
      <div className="mt-3 pt-3 border-t border-brand-borderSubtle flex items-center justify-between">
        {locked ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-secondary">
            <Lock className="h-3.5 w-3.5" />
            {item.available ? "Sblocca con il pacchetto" : "Disponibile a breve"}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold"
                style={{ color: "#008C45" }}>
            {downloading ? (
              <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Scaricamento…</>
            ) : (
              <><Download className="h-3.5 w-3.5" /> Scarica PDF</>
            )}
          </span>
        )}
        <ArrowRight className="h-3.5 w-3.5 text-brand-secondary transition-transform group-hover:translate-x-0.5" />
      </div>
    </button>
  );
}
